import React, { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { StickyNote, Save } from 'lucide-react'
import { useLifeSpaces } from '../../../hooks/useLifeSpaces'
import './QuickNotesWidget.scss'

export const QuickNotesWidget: React.FC = () => {
  const { t, i18n } = useTranslation()
  const { activeLifeSpace } = useLifeSpaces()
  const [notes, setNotes] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const [lastSaved, setLastSaved] = useState<Date | null>(null)
  const saveTimeoutRef = useRef<NodeJS.Timeout>()

  // Load notes for current life space
  useEffect(() => {
    const savedNotes = localStorage.getItem(`quicknotes_${activeLifeSpace}`)
    if (savedNotes) {
      setNotes(savedNotes)
    } else {
      setNotes('')
    }
  }, [activeLifeSpace])

  // Auto-save with debounce
  useEffect(() => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current)
    }

    if (notes !== '') {
      setIsSaving(true)
      saveTimeoutRef.current = setTimeout(() => {
        localStorage.setItem(`quicknotes_${activeLifeSpace}`, notes)
        setIsSaving(false)
        setLastSaved(new Date())
      }, 1000) // Save 1 second after stopping typing
    }

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current)
      }
    }
  }, [notes, activeLifeSpace])

  const handleNotesChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setNotes(e.target.value)
  }

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString(i18n.language, {
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getLifeSpaceName = () => {
    const key = `lifeSpaces.${activeLifeSpace}`
    const translated = t(key)
    return translated === key ? activeLifeSpace : translated
  }

  return (
    <div className="quick-notes-widget">
      <div className="quick-notes-widget__header">
        <div className="quick-notes-widget__title-group">
          <StickyNote size={16} />
          <span className="quick-notes-widget__title">{t('quickNotes.widget.title')}</span>
          <span className="quick-notes-widget__space">({getLifeSpaceName()})</span>
        </div>
        {isSaving && (
          <div className="quick-notes-widget__saving">
            <Save size={12} />
            <span>{t('quickNotes.widget.saving')}</span>
          </div>
        )}
      </div>

      <textarea
        className="quick-notes-widget__textarea"
        placeholder={t('quickNotes.widget.placeholder')}
        value={notes}
        onChange={handleNotesChange}
        spellCheck={false}
      />

      {lastSaved && (
        <div className="quick-notes-widget__footer">
          <span className="quick-notes-widget__saved-time">
            {t('quickNotes.widget.savedAt', { time: formatTime(lastSaved) })}
          </span>
        </div>
      )}
    </div>
  )
}